import CdlAExperienceFormType from "./implementations/CdlAExperienceFormType";
import MultipleLicenseExperienceFormType from "./implementations/MultipleLicenseExperienceFormType";
import MechTechFormType from "./implementations/MechTechFormType";
import DefaultFormType from "./implementations/DefaultFormType";

const FORM_TYPE_DEFAULT_SETTINGS = {
    /**
     * @param mediaFullForm The complete Form Data
     * @param mediaFormThis The `this` of the MediaForm component. Use to access needed information.
     * @returns {boolean} Whether the experience fields should be disabled
     */
    mediaFormIsExperienceDisabled: (mediaFullForm, mediaFormThis) => false,

    /**
     * @param mediaFullFormNewValues    The complete form with unapplied changes
     * @param mediaFormThis             The `this` of the MediaForm component. Use to access needed information.
     */
    mediaFormUpdated: (mediaFullFormNewValues, mediaFormThis) => undefined,

    /**
     * Returns the simple form type input fields to be displayed. The array can contain the following:
     * * An object with the keys:
     *    * name - The name of the field on the form. Also used as the id
     *    * options - Either a string 'yes-no' for simple Yes/No, or a function that receives the options data
     *                and returns the options to be used
     *    * validation - An object with the v-validate rules to use as keys, mapping to their related error messages
     *    * show - A callback receiving the form_type_field object and returning a boolean whether to show this field
     * * Or, simply the string 'experienceFields', which will insert the Experience Levels fields in that spot
     *
     * @return {*[{name: string, options: string|(function(*): []), validation: {required: string}, show: (function(*): boolean)|undefined}|string]}
     */
    getFormTypeInputFields: () => [ 'experienceFields' ],
}

const FORM_TYPE_OVERRIDES = [
    CdlAExperienceFormType,
    MultipleLicenseExperienceFormType,
    MechTechFormType,
    DefaultFormType,
]

/**
 * @param formTypeId    The Form Type ID
 * @return {{mediaFormIsExperienceDisabled: function(*, *): boolean, getFormTypeInputFields: function(): *, mediaFormUpdated: function(*, *): undefined}}
 *          The JS FormType implementation
 */
export const getFormTypeImplementation = (formTypeId) => ({
    ...FORM_TYPE_DEFAULT_SETTINGS,
    ...(FORM_TYPE_OVERRIDES.find(fti => fti.id === formTypeId) || {})
})

export const getFormTypeInputFieldsConfig = (formTypeId, allOptionsData) => {
    return getFormTypeImplementation(formTypeId)
        .getFormTypeInputFields()
        .map(formTypeInputFields => {
          // First convert "special" use case for "experienceFields"
          return formTypeInputFields === 'experienceFields'
            ? { name: formTypeInputFields }
            : formTypeInputFields;
        })
        .map((formTypeInputFields, index) => {
            // Process the objects
            if (typeof formTypeInputFields === "object") {
                return {
                    name: formTypeInputFields.name ?? `form-type-input-field-${index}`,
                    options: ((options) => {
                        if (typeof options === "function") return options(allOptionsData);
                        if (options === "yes-no") return [
                            { label: "Yes", value: "yes" },
                            { label: "No", value: "no" },
                        ];
                        return [];
                    })(formTypeInputFields.options),
                    validation: formTypeInputFields.validation ?? {},
                    show: formTypeInputFields.show ?? (() => true)
                }
            }

            return formTypeInputFields;
        })
}
